import {Alert,Box,Button,Card,CardContent,Typography} from '@mui/material';
import React,{useEffect,useMemo,useRef,useState} from 'react';
import {LessonStep} from '../../types';

interface Props {
  step: LessonStep;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
  isLastStep?: boolean;
}

interface ChoiceExercise {
  id: string;
  type: 'find_letter';
  question: string;
  options: string[];
  correct_answer: string;
}
interface PracticeSource {source: string; license: string; exercises: ChoiceExercise[];}

const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

// Validate the whole source: do not silently drop exercises or invent answers and choices.
function readSource(content: LessonStep['content']): PracticeSource | null {
  if(content.verification !== 'source_record' || !nonempty(content.source) || !nonempty(content.license) ||
     !Array.isArray(content.exercises) || content.exercises.length < 1 || content.exercises.length > 50) return null;
  const ids = new Set<string>();
  const exercises: ChoiceExercise[] = [];
  for(const item of content.exercises as unknown[]) {
    if(!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const row = item as Record<string,unknown>;
    if(!nonempty(row.id) || ids.has(row.id) || row.type !== 'find_letter' || !nonempty(row.question) ||
       !nonempty(row.correct_answer) || !Array.isArray(row.options) || row.options.length < 2 || row.options.length > 12 ||
       !row.options.every(nonempty) || new Set(row.options).size !== row.options.length || !row.options.includes(row.correct_answer)) return null;
    ids.add(row.id);
    exercises.push({id:row.id,type:'find_letter',question:row.question,options:row.options as string[],correct_answer:row.correct_answer});
  }
  return {source:content.source,license:content.license,exercises};
}

const PracticeStep: React.FC<Props> = ({step,onNext,onBackToLetters,isLastStep=false}) => {
  const source=useMemo(()=>readSource(step.content),[step.content]);
  const [index,setIndex]=useState(0);
  const [selected,setSelected]=useState<string|null>(null);
  const advancing=useRef(false);
  useEffect(()=>{setIndex(0);setSelected(null);advancing.current=false;},[step.id,source]);
  useEffect(()=>{advancing.current=false;},[index]);
  const exercise=source?.exercises[index];
  if(!source || !exercise) return <Card><CardContent>
    <Typography variant="h5">{step.title}</Typography>
    <Alert severity="error">Подтверждённые упражнения недоступны или имеют неподдерживаемый формат. Попробуйте позже.</Alert>
    {onBackToLetters&&<Button onClick={onBackToLetters}>К выбору буквы</Button>}
  </CardContent></Card>;
  const correct=selected===exercise.correct_answer;
  const advance=()=>{
    if(!correct||advancing.current)return;
    advancing.current=true;
    if(index+1<source.exercises.length){setIndex(value=>value+1);setSelected(null);}
    else onNext();
  };
  return <Card><CardContent>
    <Typography variant="h5">{step.title}</Typography>
    {typeof step.content.instruction==='string'&&step.content.instruction&&<Typography sx={{my:1}}>{step.content.instruction}</Typography>}
    <Typography variant="caption">Источник: {source.source}; лицензия: {source.license}.</Typography>
    <Typography sx={{my:2}}>Упражнение {index+1} из {source.exercises.length}</Typography>
    <Typography variant="h6">{exercise.question}</Typography>
    <Box sx={{display:'flex',flexWrap:'wrap',gap:1,my:2}}>
      {exercise.options.map(option=><Button key={option} variant={selected===option?'contained':'outlined'}
        disabled={correct} onClick={()=>setSelected(option)}>{option}</Button>)}
    </Box>
    {selected!==null&&<Alert severity={correct?'success':'warning'}>{correct?'Ответ совпадает с ответом источника.':'Ответ не совпадает с ответом источника. Попробуйте ещё раз.'}</Alert>}
    {isLastStep&&<Alert severity="info">Это последний шаг. Завершение сохранит вашу отметку о прохождении без оценки навыка.</Alert>}
    <Button variant="contained" sx={{mt:2}} disabled={!correct} onClick={advance}>
      {index+1<source.exercises.length?'Следующее упражнение':'Завершить практику'}
    </Button>
  </CardContent></Card>;
};

export default PracticeStep;
